<script lang="ts">
  // Dialog. On phones (≤ 640px) the default variant becomes a bottom sheet.
  //   <Modal bind:open title="Nieuwe klant" size="md">…{#snippet footer()}…{/snippet}</Modal>
  // Variants:
  //   default     header (title, description, ×), scrollable body, footer
  //   bare        only the surface: no header, no padding; you render everything
  //               (command palette, custom flows). Give it an `ariaLabel`.
  //   fullscreen  covers the viewport (onboarding, big editors); header/footer as default
  // `align="top"` places the dialog near the top (palettes, search).
  // Escape and a backdrop click close it unless `dismissible={false}`.
  import type { Snippet } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { X } from '@lucide/svelte';
  import { portal, trapFocus } from './actions';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    open?: boolean;
    title?: string;
    description?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'default' | 'bare' | 'fullscreen';
    align?: 'center' | 'top';
    /** Accessible name when there is no title (bare dialogs). */
    ariaLabel?: string;
    /** Close on backdrop click / Escape (default true). */
    dismissible?: boolean;
    /** Use the bottom sheet on phones (default true; not for bare/fullscreen). */
    sheet?: boolean;
    onclose?: () => void;
    header?: Snippet;
    children: Snippet;
    footer?: Snippet;
    class?: string;
  }
  let {
    open = $bindable(false),
    title,
    description,
    size = 'md',
    variant = 'default',
    align = 'center',
    ariaLabel,
    dismissible = true,
    sheet = true,
    onclose,
    header,
    children,
    footer,
    class: cls = ''
  }: Props = $props();

  let mobile = $state(typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  $effect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => (mobile = mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  });
  const asSheet = $derived(mobile && sheet && variant === 'default');

  function close() {
    if (!dismissible) return;
    open = false;
    onclose?.();
  }

  $effect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  });
</script>

{#if open}
  <div use:portal class="layer {variant}" class:top={align === 'top'}>
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="backdrop" transition:fade={{ duration: 140 }} onclick={close}></div>
    <div
      class="dialog {size} {variant} {cls}"
      class:sheet={asSheet}
      role="dialog"
      aria-modal="true"
      aria-label={title || ariaLabel}
      tabindex="-1"
      use:trapFocus
      onkeydown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}
      in:fly={asSheet ? { y: 40, duration: 220, easing: cubicOut } : { y: 8, duration: 180, easing: cubicOut }}
      out:fade={{ duration: 120 }}
    >
      {#if variant === 'bare'}
        {@render children()}
      {:else}
        {#if header}
          {@render header()}
        {:else if title}
          <header class="head">
            <div class="titles">
              <h2>{title}</h2>
              {#if description}<p>{description}</p>{/if}
            </div>
            {#if dismissible}
              <button class="x" type="button" aria-label={t('ui.close')} onclick={close}><X /></button>
            {/if}
          </header>
        {/if}
        <div class="body">{@render children()}</div>
        {#if footer}<footer class="foot">{@render footer()}</footer>{/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .layer {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    display: grid;
    place-items: center;
    padding: var(--space-6);
  }
  .layer.top { align-items: start; padding-top: min(12vh, 120px); }
  .layer.fullscreen { padding: 0; }
  .backdrop { position: absolute; inset: 0; background: var(--overlay); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  .dialog {
    position: relative;
    width: 100%;
    max-height: calc(100dvh - 2 * var(--space-6));
    display: flex;
    flex-direction: column;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    outline: none;
  }
  .sm { max-width: 420px; }
  .md { max-width: 560px; }
  .lg { max-width: 760px; }
  .xl { max-width: 1040px; }
  .dialog.bare { overflow: hidden; }
  .dialog.fullscreen {
    max-width: none;
    width: 100%;
    height: 100dvh;
    max-height: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    background: var(--bg);
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    max-width: none;
    max-height: 92dvh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); padding: var(--space-5) var(--space-5) 0; }
  .fullscreen .head { padding: var(--space-4) var(--space-6); border-bottom: 1px solid var(--border); background: var(--surface); }
  .titles { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  .titles h2 { font-size: var(--text-lg); }
  .titles p { color: var(--text-2); font-size: var(--text-base); }
  .x {
    border: 0;
    background: transparent;
    color: var(--text-3);
    width: 32px;
    height: 32px;
    border-radius: var(--radius-xs);
    display: grid;
    place-items: center;
    margin: calc(var(--space-1) * -1) calc(var(--space-1_5) * -1) 0 0;
    flex-shrink: 0;
  }
  .x :global(svg) { width: var(--icon-lg); height: var(--icon-lg); }
  .x:hover { background: var(--surface-hover); color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }
  .body { padding: var(--space-4) var(--space-5) var(--space-5); overflow-y: auto; min-height: 0; }
  .fullscreen .body { flex: 1; padding: var(--space-6); }
  .foot { display: flex; justify-content: flex-end; gap: var(--space-2); padding: var(--space-3) var(--space-5); border-top: 1px solid var(--border); flex-wrap: wrap; }
  .fullscreen .foot { background: var(--surface); }
  @media (max-width: 640px) {
    .layer { padding: 0; }
    .layer.top { padding: var(--space-3); }
    .dialog.bare { max-height: calc(100dvh - 2 * var(--space-3)); }
    .fullscreen .body { padding: var(--space-4); }
    .foot :global(.btn) { flex: 1; }
  }
</style>
